import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Reply, SquarePen, Star, Trash2 } from "lucide-react";
import { initials } from "@clinic/ui";
import { ROLE_LABELS, type MailFolder, type MailSummary, type MailUserLite } from "@clinic/types";
import { api } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FieldError, Input, Label, Textarea } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

interface ComposeSeed {
  recipientIds: string[];
  subject: string;
  body: string;
  parentId?: string;
}

export function MailPage() {
  const queryClient = useQueryClient();
  const [folder, setFolder] = useState<MailFolder>("inbox");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [compose, setCompose] = useState<ComposeSeed | null>(null);

  const inbox = useQuery({
    queryKey: ["mail", "inbox"],
    queryFn: () => api.mail.inbox(),
    refetchInterval: 8000,
    enabled: folder === "inbox",
  });
  const sent = useQuery({
    queryKey: ["mail", "sent"],
    queryFn: () => api.mail.sent(),
    enabled: folder === "sent",
  });

  const list = folder === "inbox" ? inbox : sent;

  const refreshLists = () => {
    queryClient.invalidateQueries({ queryKey: ["mail", "inbox"] });
    queryClient.invalidateQueries({ queryKey: ["mail", "sent"] });
    queryClient.invalidateQueries({ queryKey: ["mail", "unread"] });
  };

  return (
    <div className="flex h-[calc(100vh-9rem)] flex-col">
      <PageHeader
        title="Mail"
        subtitle="Internal email — stays inside the clinic"
        actions={
          <Button
            size="sm"
            onClick={() => setCompose({ recipientIds: [], subject: "", body: "" })}
          >
            <SquarePen size={15} /> Compose
          </Button>
        }
      />

      <Tabs value={folder} onValueChange={(v) => { setFolder(v as MailFolder); setSelectedId(null); }}>
        <TabsList>
          <TabsTrigger value="inbox">Inbox</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
        </TabsList>
      </Tabs>

      <div
        className="flex min-h-0 flex-1 overflow-hidden rounded-card border bg-bg-secondary"
        style={{ borderColor: "var(--accent-subtle)" }}
      >
        {/* list */}
        <div
          className="flex w-96 shrink-0 flex-col border-r"
          style={{ borderColor: "var(--accent-subtle)" }}
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
            {list.isLoading ? (
              <div className="space-y-2 p-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-16" />
                ))}
              </div>
            ) : list.data && list.data.length > 0 ? (
              list.data.map((mail) => (
                <MailRow
                  key={mail.id}
                  mail={mail}
                  folder={folder}
                  active={mail.id === selectedId}
                  onSelect={() => setSelectedId(mail.id)}
                />
              ))
            ) : (
              <p className="px-4 py-10 text-center text-sm text-text-tertiary">
                {folder === "inbox" ? "Your inbox is empty." : "You haven't sent anything yet."}
              </p>
            )}
          </div>
        </div>

        {/* reading pane */}
        <div className="flex min-w-0 flex-1 flex-col">
          {selectedId ? (
            <MailReader
              key={selectedId}
              mailId={selectedId}
              folder={folder}
              onChanged={refreshLists}
              onDeleted={() => {
                setSelectedId(null);
                refreshLists();
              }}
              onReply={(seed) => setCompose(seed)}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center p-8 text-center text-text-tertiary">
              Select a message to read it.
            </div>
          )}
        </div>
      </div>

      {compose && (
        <ComposeDialog
          seed={compose}
          onClose={() => setCompose(null)}
          onSent={() => {
            setCompose(null);
            setFolder("sent");
            refreshLists();
          }}
        />
      )}
    </div>
  );
}

function MailRow({
  mail,
  folder,
  active,
  onSelect,
}: {
  mail: MailSummary;
  folder: MailFolder;
  active: boolean;
  onSelect: () => void;
}) {
  const who = folder === "inbox" ? mail.sender.name : `To: ${mail.recipients.map((r) => r.name).join(", ")}`;
  const unread = folder === "inbox" && !mail.read;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full flex-col gap-0.5 border-b px-4 py-3 text-left transition",
        active ? "bg-accent-subtle" : "hover:bg-bg-tertiary",
      )}
      style={{ borderColor: "var(--accent-subtle)" }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={cn("flex items-center gap-1.5 truncate text-sm", unread ? "font-semibold" : "text-text-secondary")}>
          {unread && <span className="h-2 w-2 shrink-0 rounded-full bg-accent" />}
          {mail.starred && <Star size={12} className="shrink-0 fill-warning text-warning" />}
          {who}
        </span>
        <span className="shrink-0 text-[11px] text-text-tertiary">
          {format(new Date(mail.createdAt), "d MMM")}
        </span>
      </div>
      <span className={cn("truncate text-sm", unread ? "font-medium" : "text-text-secondary")}>
        {mail.subject}
      </span>
      <span className="truncate text-xs text-text-tertiary">{mail.snippet}</span>
    </button>
  );
}

function MailReader({
  mailId,
  folder,
  onChanged,
  onDeleted,
  onReply,
}: {
  mailId: string;
  folder: MailFolder;
  onChanged: () => void;
  onDeleted: () => void;
  onReply: (seed: ComposeSeed) => void;
}) {
  const queryClient = useQueryClient();
  const mail = useQuery({
    queryKey: ["mail", "detail", mailId],
    queryFn: () => api.mail.get(mailId),
  });

  // Opening a message marks it read on the server; refresh the list badges.
  useEffect(() => {
    onChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailId, mail.data?.read]);

  const star = useMutation({
    mutationFn: (next: boolean) => (next ? api.mail.star(mailId) : api.mail.unstar(mailId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mail", "detail", mailId] });
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.mail.remove(mailId),
    onSuccess: onDeleted,
  });

  if (mail.isLoading) {
    return <div className="p-6"><Skeleton className="h-40" /></div>;
  }
  if (!mail.data) {
    return <div className="flex flex-1 items-center justify-center text-text-tertiary">Message not found.</div>;
  }
  const m = mail.data;

  return (
    <>
      <div
        className="flex items-start justify-between gap-3 border-b px-6 py-4"
        style={{ borderColor: "var(--accent-subtle)" }}
      >
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">{m.subject}</h2>
          <div className="mt-1 flex items-center gap-2 text-sm text-text-secondary">
            <div className="flex h-7 w-7 items-center justify-center rounded-pill bg-accent-subtle text-[11px] font-semibold text-accent-light">
              {initials(m.sender.name)}
            </div>
            <span>
              <span className="font-medium text-text">{m.sender.name}</span>{" "}
              <span className="text-text-tertiary">→ {m.recipients.map((r) => r.name).join(", ")}</span>
            </span>
          </div>
          <div className="mt-1 text-xs text-text-tertiary">
            {format(new Date(m.createdAt), "EEE d MMM yyyy, HH:mm")}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {folder === "inbox" && (
            <Button
              variant="ghost"
              size="icon"
              title={m.starred ? "Unstar" : "Star"}
              onClick={() => star.mutate(!m.starred)}
            >
              <Star size={15} className={cn(m.starred && "fill-warning text-warning")} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            title="Delete"
            onClick={() => remove.mutate()}
            loading={remove.isPending}
          >
            <Trash2 size={15} />
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{m.body}</p>
      </div>

      <div className="border-t px-6 py-3" style={{ borderColor: "var(--accent-subtle)" }}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            onReply({
              recipientIds: [m.sender.id],
              subject: m.subject.startsWith("Re:") ? m.subject : `Re: ${m.subject}`,
              body: `\n\n— On ${format(new Date(m.createdAt), "d MMM yyyy")}, ${m.sender.name} wrote:\n${m.body
                .split("\n")
                .map((l) => `> ${l}`)
                .join("\n")}`,
              parentId: m.id,
            })
          }
        >
          <Reply size={14} /> Reply
        </Button>
      </div>
    </>
  );
}

function ComposeDialog({
  seed,
  onClose,
  onSent,
}: {
  seed: ComposeSeed;
  onClose: () => void;
  onSent: () => void;
}) {
  const me = useMe();
  const [recipientIds, setRecipientIds] = useState<string[]>(seed.recipientIds);
  const [subject, setSubject] = useState(seed.subject);
  const [body, setBody] = useState(seed.body);

  const directory = useQuery({
    queryKey: ["directory"],
    queryFn: () => api.users.directory(),
  });

  const send = useMutation({
    mutationFn: () =>
      api.mail.send({ recipientIds, subject: subject.trim(), body: body.trim(), parentId: seed.parentId }),
    onSuccess: onSent,
  });

  const toggle = (id: string) =>
    setRecipientIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const canSend = recipientIds.length > 0 && subject.trim().length > 0 && body.trim().length > 0;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={seed.parentId ? "Reply" : "New message"} className="max-w-2xl">
        <div className="space-y-4">
          <div>
            <Label>To</Label>
            <div
              className="max-h-32 space-y-1 overflow-y-auto rounded-lg border p-2"
              style={{ borderColor: "var(--accent-subtle)" }}
            >
              {(directory.data ?? []).map((u: MailUserLite) => (
                <label
                  key={u.id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-bg-tertiary"
                >
                  <input
                    type="checkbox"
                    checked={recipientIds.includes(u.id)}
                    onChange={() => toggle(u.id)}
                    className="h-4 w-4 accent-[var(--accent)]"
                  />
                  <span className="text-sm">{u.name}</span>
                  <span className="text-xs text-text-tertiary">· {ROLE_LABELS[u.role]}</span>
                </label>
              ))}
              {me.data && directory.data?.length === 0 && (
                <p className="px-2 py-3 text-sm text-text-tertiary">No other staff to send to.</p>
              )}
            </div>
          </div>
          <div>
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="min-h-[160px]"
            />
          </div>
          {send.isError && (
            <FieldError
              message={send.error instanceof Error ? send.error.message : "Failed to send"}
            />
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => send.mutate()} disabled={!canSend} loading={send.isPending}>
              Send
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
